import {
  PROFILE_PLAN_LIST_VIEWS,
  type ProfilePlanListView,
} from './PlanListViewTabs';
import {
  savedPlanMatchesListView,
  type PlanCardSourceMeta,
} from './profilePlanListViewUtils';
import type {
  ProfilePlanLibrary,
  ProfilePlanLibrarySource,
  PlanStudioSavedPlan,
} from './types';

export type ProfilePlanLibraryCache = Partial<
  Record<ProfilePlanListView, ProfilePlanLibrary>
>;
export type ProfileSourceViewById = Record<string, ProfilePlanListView>;
export type ProfileSourceLoadingByView = Record<ProfilePlanListView, boolean>;
export type ProfileSourceLoadedProfileByView = Record<
  ProfilePlanListView,
  string | null
>;

export const DEFAULT_PROFILE_SOURCE_VIEW: ProfilePlanListView = 'active';

export const filterDeletedGroupSources = (
  library: ProfilePlanLibrary,
  deletedGroupIds: ReadonlySet<string>
): ProfilePlanLibrary => {
  const sources = library.sources.filter(
    (source) =>
      source.source_type !== 'group' || !deletedGroupIds.has(source.source_id)
  );
  return sources.length === library.sources.length
    ? library
    : { ...library, sources };
};

export const createProfileSourceLoadingByView =
  (): ProfileSourceLoadingByView => ({
    active: false,
    inactive: false,
    archived: false,
    history: false,
  });

export const createProfileSourceLoadedProfileByView =
  (): ProfileSourceLoadedProfileByView => ({
    active: null,
    inactive: null,
    archived: null,
    history: null,
  });

export const libraryLessonPlans = (
  library: ProfilePlanLibrary | null
): PlanStudioSavedPlan[] =>
  library
    ? library.sources.flatMap((source) =>
        source.source_type === 'profile' || source.enabled_for_profile
          ? source.plans
          : []
      )
    : [];

export const libraryPlanMetaById = (
  library: ProfilePlanLibrary | null,
  profileCanManage: boolean
): Record<string, PlanCardSourceMeta> => {
  if (!library) {
    return {};
  }

  return library.sources.reduce<Record<string, PlanCardSourceMeta>>(
    (record, source) => {
      source.plans.forEach((plan) => {
        record[plan.id] = {
          sourceType: source.source_type,
          sourceName: source.name || source.source_id,
          sourceAccess:
            source.source_type === 'profile' && !profileCanManage
              ? 'read'
              : source.access,
          enabledForProfile: source.enabled_for_profile,
        };
      });
      return record;
    },
    {}
  );
};

export const updateLibraryPlan = (
  library: ProfilePlanLibrary | null,
  plan: PlanStudioSavedPlan,
  visibleView: ProfilePlanListView
): ProfilePlanLibrary | null => {
  if (!library) {
    return library;
  }

  return {
    ...library,
    sources: library.sources.map((source) => {
      if (
        source.source_type !== plan.target_type ||
        source.source_id !== plan.target_id
      ) {
        return source;
      }

      const hasPlan = source.plans.some((item) => item.id === plan.id);
      const matchesCurrentView = savedPlanMatchesListView(plan, visibleView);
      const nextPlans = matchesCurrentView
        ? hasPlan
          ? source.plans.map((item) => (item.id === plan.id ? plan : item))
          : [plan, ...source.plans]
        : source.plans.filter((item) => item.id !== plan.id);

      return {
        ...source,
        plans: nextPlans,
      };
    }),
  };
};

export const updateLibrarySourceEnabled = (
  library: ProfilePlanLibrary | null,
  sourceId: string,
  enabled: boolean
): ProfilePlanLibrary | null =>
  library
    ? {
        ...library,
        sources: library.sources.map((source) =>
          source.source_type === 'group' && source.source_id === sourceId
            ? { ...source, enabled_for_profile: enabled }
            : source
        ),
      }
    : library;

export const mapProfileSourceLibraryCache = (
  cache: ProfilePlanLibraryCache,
  updater: (
    library: ProfilePlanLibrary,
    view: ProfilePlanListView
  ) => ProfilePlanLibrary
): ProfilePlanLibraryCache => {
  const next: ProfilePlanLibraryCache = {};

  PROFILE_PLAN_LIST_VIEWS.forEach((view) => {
    const library = cache[view];
    if (library) {
      next[view] = updater(library, view);
    }
  });

  return next;
};

const sourceCacheKey = (
  source: Pick<ProfilePlanLibrarySource, 'source_type' | 'source_id'>
): string => `${source.source_type}:${source.source_id}`;

export const findLibrarySource = (
  library: ProfilePlanLibrary | undefined,
  target: Pick<ProfilePlanLibrarySource, 'source_type' | 'source_id'>
): ProfilePlanLibrarySource | undefined =>
  library?.sources.find(
    (source) =>
      source.source_type === target.source_type &&
      source.source_id === target.source_id
  );

export const findCachedLibrarySource = (
  cache: ProfilePlanLibraryCache,
  target: Pick<ProfilePlanLibrarySource, 'source_type' | 'source_id'>
): ProfilePlanLibrarySource | undefined => {
  for (const view of PROFILE_PLAN_LIST_VIEWS) {
    const source = findLibrarySource(cache[view], target);
    if (source) {
      return source;
    }
  }

  return undefined;
};

export const findLibraryPlan = (
  library: ProfilePlanLibrary | undefined | null,
  targetType: PlanStudioSavedPlan['target_type'],
  targetId: string | null | undefined,
  planId: string | undefined
): PlanStudioSavedPlan | undefined => {
  if (!library || !targetId || !planId) {
    return undefined;
  }

  return library.sources
    .flatMap((source) => source.plans)
    .find(
      (plan) =>
        plan.id === planId &&
        plan.target_type === targetType &&
        plan.target_id === targetId
    );
};

export const findCachedSourcePlan = (
  cache: ProfilePlanLibraryCache,
  targetType: PlanStudioSavedPlan['target_type'],
  targetId: string | null | undefined,
  planId: string | undefined
): PlanStudioSavedPlan | undefined => {
  for (const view of PROFILE_PLAN_LIST_VIEWS) {
    const plan = findLibraryPlan(cache[view], targetType, targetId, planId);
    if (plan) {
      return plan;
    }
  }

  return undefined;
};

export const firstProfileSourceLibrary = (
  cache: ProfilePlanLibraryCache
): ProfilePlanLibrary | null => {
  for (const view of PROFILE_PLAN_LIST_VIEWS) {
    const library = cache[view];
    if (library) {
      return library;
    }
  }

  return null;
};

export const groupSourcesFromProfileSourceCache = (
  cache: ProfilePlanLibraryCache,
  sourceViewById: ProfileSourceViewById
): ProfilePlanLibrarySource[] => {
  const sourcesByKey = new Map<string, ProfilePlanLibrarySource>();

  PROFILE_PLAN_LIST_VIEWS.forEach((view) => {
    cache[view]?.sources.forEach((source) => {
      if (source.source_type !== 'group') {
        return;
      }

      const key = sourceCacheKey(source);
      if (!sourcesByKey.has(key)) {
        sourcesByKey.set(key, source);
      }
    });
  });

  return Array.from(sourcesByKey.values()).map((source) => {
    const selectedView =
      sourceViewById[source.source_id] ?? DEFAULT_PROFILE_SOURCE_VIEW;
    const selectedSource = findLibrarySource(cache[selectedView], source);

    return selectedSource ?? { ...source, plans: [] };
  });
};

export const inProgressGroupSourceIdsFromCache = (
  cache: ProfilePlanLibraryCache
): Set<string> => {
  const sourceIds = new Set<string>();

  PROFILE_PLAN_LIST_VIEWS.forEach((view) => {
    cache[view]?.sources.forEach((source) => {
      if (
        source.source_type === 'group' &&
        source.plans.some((plan) => plan.assignment?.state === 'in_progress')
      ) {
        sourceIds.add(source.source_id);
      }
    });
  });

  return sourceIds;
};
